import { useEffect, useMemo, useRef, useState } from 'react'
import maplibregl, { type GeoJSONSource, type Map as MlMap } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { materialColor, materialColorExpression } from '@/features/rtk/materials'
import { fmtDate, fmtNumber, useT } from '@/i18n'
import { crewsMessages } from '@/i18n/messages/crews'
import type { CrewRoute } from './types'

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches

/**
 * Everything the crew measured with RTK, coloured by material like the RTK map, with a
 * day-by-day replay: each recorded day adds its routes, so the crew's path across sites (and
 * how much it laid where) plays out in a few seconds.
 */
export function CrewMap({ routes }: { routes: CrewRoute[] }) {
  const t = useT(crewsMessages)
  const node = useRef<HTMLDivElement>(null)
  const map = useRef<MlMap | null>(null)
  const [ready, setReady] = useState(false)
  const dark = useMemo(prefersDark, [])

  // Distinct measured days, oldest first; each feature carries its day's index for the filter.
  const days = useMemo(() => [...new Set(routes.map((r) => r.measured_at.slice(0, 10)))].sort(), [routes])
  const [dayIndex, setDayIndex] = useState(days.length - 1)
  const [playing, setPlaying] = useState(false)

  const data = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: routes.map((r) => ({
      type: 'Feature',
      geometry: r.geojson,
      properties: { material: r.material_name ?? '', day: days.indexOf(r.measured_at.slice(0, 10)) },
    })),
  }), [routes, days])

  useEffect(() => {
    if (!node.current) return
    const m = new maplibregl.Map({
      container: node.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256,
            attribution: '© OpenStreetMap contributors', maxzoom: 19,
          },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm', paint: { 'raster-saturation': -0.6, 'raster-opacity': 0.7 } }],
      },
      center: [10.4, 51.2],
      zoom: 5,
      attributionControl: { compact: true },
      cooperativeGestures: true,
    })
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    m.on('load', () => {
      m.addSource('routes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      m.addLayer({
        id: 'lines', type: 'line', source: 'routes', filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': materialColorExpression(dark), 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2, 17, 5] },
      })
      m.addLayer({
        id: 'points', type: 'circle', source: 'routes', filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-color': materialColorExpression(dark), 'circle-radius': 4,
          'circle-stroke-color': dark ? '#111' : '#fff', 'circle-stroke-width': 1.5,
        },
      })
      setReady(true)
    })
    map.current = m
    return () => { m.remove(); map.current = null }
  }, [dark])

  // Data, then frame it once.
  useEffect(() => {
    const m = map.current
    if (!ready || !m) return
    ;(m.getSource('routes') as GeoJSONSource).setData(data)
    const b = new maplibregl.LngLatBounds()
    for (const r of routes) {
      if (r.geojson.type === 'Point') b.extend(r.geojson.coordinates)
      else for (const c of r.geojson.coordinates) b.extend(c)
    }
    if (!b.isEmpty()) m.fitBounds(b, { padding: 40, maxZoom: 16, duration: 0 })
  }, [ready, data, routes])

  // Replay: show only the days up to the current one.
  useEffect(() => {
    const m = map.current
    if (!ready || !m) return
    const f: maplibregl.FilterSpecification = ['<=', ['get', 'day'], dayIndex]
    m.setFilter('lines', ['all', ['==', ['geometry-type'], 'LineString'], f])
    m.setFilter('points', ['all', ['==', ['geometry-type'], 'Point'], f])
  }, [ready, dayIndex])

  useEffect(() => {
    if (!playing) return
    if (dayIndex >= days.length - 1) { setPlaying(false); return }
    const id = window.setTimeout(() => setDayIndex((i) => i + 1), days.length > 60 ? 120 : 350)
    return () => window.clearTimeout(id)
  }, [playing, dayIndex, days.length])

  const play = () => {
    if (dayIndex >= days.length - 1) setDayIndex(0)
    setPlaying((p) => !p)
  }

  const shown = routes.filter((r) => days.indexOf(r.measured_at.slice(0, 10)) <= dayIndex)
  const metres = shown.reduce((s, r) => s + (r.geojson.type === 'LineString' ? r.length_m ?? 0 : 0), 0)
  const materials = [...new Set(routes.map((r) => r.material_name ?? ''))].filter(Boolean).sort()

  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <div ref={node} className="h-[22rem] w-full sm:h-[28rem]" />
      <div className="space-y-2 p-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={play}
            aria-label={playing ? t('mapPause') : t('mapReplay')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white hover:bg-brand-700"
          >
            {playing ? '❚❚' : '▶'}
          </button>
          <input
            type="range" min={0} max={Math.max(days.length - 1, 0)} value={dayIndex}
            onChange={(e) => { setPlaying(false); setDayIndex(Number(e.target.value)) }}
            aria-label={t('mapTimeline')}
            className="min-w-0 flex-1 accent-brand-600"
          />
          <span className="tabular shrink-0 text-xs text-neutral-500">
            {fmtDate(days[dayIndex])} · {fmtNumber(metres)} m
          </span>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
          {materials.map((m) => (
            <span key={m} className="inline-flex items-center gap-1">
              <span aria-hidden className="inline-block h-2 w-3 rounded-sm" style={{ backgroundColor: materialColor(m, dark) }} />
              {m}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
